var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { z } from "zod";
import { SurveyElementTypeEnum, SurveyInputTypeEnum, ValidatorType, } from "../../../types/forms";
import { validateValidatorsPageElements } from "../../../utils/elements-condition";
import { checkMinMaxViolation, isMinMaxInputType, } from "../../../utils/date-limits";
import { getElementValueKey } from "../../../utils/helpers";
import { isColumnReadOnly, validateMatrixDropdownRequiredColumns, validateMatrixDropdownUniqueColumns, validateMatrixDynamicCells, validateMatrixDynamicUniqueColumns, } from "./matrix-components";
var getResolvedLimits = function (element, overrides) {
    var _a, _b;
    return ({
        min: (_a = overrides.min) !== null && _a !== void 0 ? _a : element.min,
        max: (_b = overrides.max) !== null && _b !== void 0 ? _b : element.max,
    });
};
var withMinMaxValidation = function (schema, element, limits) {
    if (limits.min === undefined && limits.max === undefined) {
        return schema;
    }
    return schema.superRefine(function (value, ctx) {
        var violation = checkMinMaxViolation(element, value, limits);
        if (violation) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: violation.message,
            });
        }
    });
};
var getRequiredErrorMessage = function (element) {
    return element.requiredErrorText || "".concat(element.title || "Este campo", " \u00E9 obrigat\u00F3rio");
};
var getUniqueColumnErrorMessage = function (element) {
    var _a;
    return ((_a = element.keyDuplicationError) === null || _a === void 0 ? void 0 : _a.trim())
        ? element.keyDuplicationError
        : "Existem valores duplicados em uma coluna que exige respostas únicas.";
};
var matrixNeedsRequiredTest = function (isRequired, columns, columnRequiredByRequiredIf) {
    return (isRequired ||
        columns.some(function (column) { return column.isRequired; }) ||
        (columnRequiredByRequiredIf !== undefined &&
            Object.values(columnRequiredByRequiredIf).some(Boolean)));
};
var matrixNeedsUniqueTest = function (columns, fieldReadOnly, matrixColumnDisabledByEnableIf) {
    return columns.some(function (column) {
        return column.isUnique &&
            !isColumnReadOnly(column, fieldReadOnly, matrixColumnDisabledByEnableIf);
    });
};
var buildBaseFieldValidation = function (element, overrides) {
    var _a, _b;
    var type = element.type, inputType = element.inputType, maxLength = element.maxLength, choices = element.choices, minSelectedChoices = element.minSelectedChoices;
    var elementHasChoices = (choices && choices.length > 0) ||
        element.showOtherItem ||
        element.showNoneItem;
    var isRequired = (_a = overrides.isRequired) !== null && _a !== void 0 ? _a : element.isRequired;
    var readOnly = (_b = overrides.readOnly) !== null && _b !== void 0 ? _b : element.readOnly;
    var matrixColumnDisabledByEnableIf = overrides.matrixColumnDisabledByEnableIf;
    var matrixColumnRequiredByRequiredIf = overrides.matrixColumnRequiredByRequiredIf;
    if ((type === SurveyElementTypeEnum.TEXT &&
        inputType !== SurveyInputTypeEnum.NUMBER) ||
        type === SurveyElementTypeEnum.COMMENT) {
        var schema_1 = z.string();
        if (readOnly)
            return schema_1;
        if (isRequired) {
            schema_1 = schema_1.min(1, getRequiredErrorMessage(element));
        }
        if (maxLength) {
            schema_1 = schema_1.max(maxLength, "M\u00E1ximo de ".concat(maxLength, " caracteres"));
        }
        if (inputType === SurveyInputTypeEnum.EMAIL) {
            schema_1 = schema_1.email("Email inválido");
        }
        if (inputType === SurveyInputTypeEnum.URL) {
            schema_1 = schema_1.url("URL inválida");
        }
        if (isMinMaxInputType(inputType)) {
            return withMinMaxValidation(schema_1, element, getResolvedLimits(element, overrides));
        }
        return schema_1;
    }
    if (type === SurveyElementTypeEnum.TEXT &&
        inputType === SurveyInputTypeEnum.NUMBER) {
        var schema_2 = z.coerce.number({
            invalid_type_error: "Deve ser um número",
        });
        if (readOnly)
            return schema_2;
        if (isRequired) {
            schema_2 = schema_2.refine(function (v) { return v !== undefined && v !== null && !isNaN(v); }, getRequiredErrorMessage(element));
        }
        var _c = getResolvedLimits(element, overrides), min_1 = _c.min, max_1 = _c.max;
        if (min_1 !== undefined) {
            schema_2 = schema_2.refine(function (v) { return v >= Number(min_1); }, "Valor m\u00EDnimo: ".concat(min_1));
        }
        if (max_1 !== undefined) {
            schema_2 = schema_2.refine(function (v) { return v <= Number(max_1); }, "Valor m\u00E1ximo: ".concat(max_1));
        }
        return schema_2;
    }
    if (type === SurveyElementTypeEnum.DROPDOWN ||
        type === SurveyElementTypeEnum.RADIOGROUP) {
        var schema_3 = z.string();
        if (readOnly)
            return schema_3;
        if (isRequired && elementHasChoices) {
            schema_3 = schema_3.min(1, getRequiredErrorMessage(element));
        }
        return schema_3;
    }
    if (type === SurveyElementTypeEnum.CHECKBOX ||
        type === SurveyElementTypeEnum.TAGBOX) {
        var schema_4 = z.array(z.string());
        if (readOnly)
            return schema_4;
        if (isRequired && elementHasChoices) {
            schema_4 = schema_4.min(1, getRequiredErrorMessage(element));
        }
        if (minSelectedChoices !== undefined) {
            return schema_4.superRefine(function (value, ctx) {
                if (!Array.isArray(value)) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: "Selecione no m\u00EDnimo ".concat(minSelectedChoices, " op\u00E7\u00F5es"),
                    });
                    return;
                }
                var selectedChoicesCount = value.filter(function (item) { return item !== "selectAll"; }).length;
                if (selectedChoicesCount < minSelectedChoices) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: "Selecione no m\u00EDnimo ".concat(minSelectedChoices, " op\u00E7\u00F5es"),
                    });
                }
            });
        }
        return schema_4;
    }
    if (type === "boolean") {
        return z.boolean();
    }
    if (type === SurveyElementTypeEnum.FILE) {
        var fileShape = z.object({
            name: z.string().min(1, "Nome do arquivo é obrigatório"),
            type: z.string().min(1, "Tipo do arquivo é obrigatório"),
            content: z
                .string()
                .min(1, "O conteúdo do arquivo é obrigatório")
                .regex(/^data:.+;base64,.+/, "O arquivo selecionado é inválido. Tente enviar o arquivo novamente."),
        });
        var arraySchema = z.array(fileShape);
        if (isRequired && !readOnly) {
            arraySchema = arraySchema.min(1, getRequiredErrorMessage(element));
        }
        return arraySchema.default([]);
    }
    if (type === SurveyElementTypeEnum.IMAGEPICKER) {
        var schema_5 = z.any();
        if (readOnly)
            return schema_5;
        if (isRequired && elementHasChoices) {
            var elementChoices = choices;
            var choicesValues_1 = elementChoices.map(function (choice) { return choice.value; });
            if (element.multiSelect) {
                schema_5 = z
                    .array(z.string().refine(function (v) { return choicesValues_1.includes(v); }, {
                    message: "Selecione opções válidas",
                }))
                    .min(1, getRequiredErrorMessage(element));
            }
            else {
                schema_5 = z
                    .string()
                    .refine(function (v) { return choicesValues_1.includes(v); }, {
                    message: "Selecione uma opção válida",
                })
                    .refine(function (v) { return v !== undefined && v !== ""; }, {
                    message: getRequiredErrorMessage(element),
                });
            }
        }
        return schema_5;
    }
    if (type === SurveyElementTypeEnum.SIGNATUREPAD) {
        var signatureShape = z.object({
            name: z.string().min(1, "Nome do arquivo é obrigatório"),
            type: z.string().min(1, "Tipo do arquivo é obrigatório"),
            content: z
                .string()
                .min(1, "A assinatura é obrigatória")
                .regex(/^data:.+;base64,.+/, "A assinatura registrada é inválida. Tente assinar novamente."),
        });
        var nullableSchema = signatureShape.nullable();
        if (readOnly)
            return nullableSchema;
        if (isRequired) {
            return nullableSchema.refine(function (v) { return v !== null; }, getRequiredErrorMessage(element));
        }
        return nullableSchema;
    }
    if (type === SurveyElementTypeEnum.RATING) {
        var schema_6 = z.coerce
            .number({ invalid_type_error: "Deve ser um número" })
            .nullable();
        if (isRequired && !readOnly) {
            schema_6 = schema_6.refine(function (v) { return v !== null && !isNaN(v); }, getRequiredErrorMessage(element));
        }
        return schema_6;
    }
    if (type === SurveyElementTypeEnum.RANKING) {
        var schema_7 = z.array(z.string());
        if (readOnly)
            return schema_7;
        if (isRequired && elementHasChoices) {
            schema_7 = schema_7.min(1, getRequiredErrorMessage(element));
        }
        return schema_7;
    }
    if (type === SurveyElementTypeEnum.MATRIX) {
        var schema_8 = z.object({}).passthrough();
        if (readOnly)
            return schema_8;
        if (isRequired || element.isAllRowRequired) {
            schema_8 = schema_8.superRefine(function (value, ctx) {
                if (!value || typeof value !== "object") {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: getRequiredErrorMessage(element),
                    });
                    return;
                }
                for (var _i = 0, _a = Object.keys(value); _i < _a.length; _i++) {
                    var key = _a[_i];
                    if (value[key] === "" ||
                        value[key] == null) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: getRequiredErrorMessage(element),
                        });
                        return;
                    }
                }
            });
        }
        if (element.eachRowUnique) {
            schema_8 = schema_8.superRefine(function (value, ctx) {
                if (!value || typeof value !== "object")
                    return;
                var values = Object.values(value);
                var filteredValues = values.filter(function (v) { return v !== "" && v != null; });
                var set = new Set(filteredValues);
                if (set.size !== filteredValues.length) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: "Cada linha deve ter uma resposta diferente.",
                    });
                }
            });
        }
        return schema_8;
    }
    if (type === SurveyElementTypeEnum.MATRIXDROPDOWN) {
        var columns_1 = (element.columns || []);
        var fieldReadOnly_1 = Boolean(readOnly);
        var elementRequired_1 = Boolean(isRequired);
        if (fieldReadOnly_1) {
            return z.object({}).passthrough();
        }
        var schema_9 = z.object({}).passthrough();
        var needsRequiredTest = matrixNeedsRequiredTest(elementRequired_1, columns_1, matrixColumnRequiredByRequiredIf);
        if (needsRequiredTest) {
            schema_9 = schema_9.superRefine(function (value, ctx) {
                var valid = validateMatrixDropdownRequiredColumns(value, element, columns_1, elementRequired_1, fieldReadOnly_1, matrixColumnDisabledByEnableIf, matrixColumnRequiredByRequiredIf);
                if (!valid) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: getRequiredErrorMessage(element),
                    });
                }
            });
        }
        var needsUniqueTest = matrixNeedsUniqueTest(columns_1, fieldReadOnly_1, matrixColumnDisabledByEnableIf);
        if (needsUniqueTest) {
            schema_9 = schema_9.superRefine(function (value, ctx) {
                var valid = validateMatrixDropdownUniqueColumns(value, element, columns_1, fieldReadOnly_1, matrixColumnDisabledByEnableIf);
                if (!valid) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: getUniqueColumnErrorMessage(element),
                    });
                }
            });
        }
        return schema_9;
    }
    if (type === SurveyElementTypeEnum.MATRIXDYNAMIC) {
        var columns_2 = (element.columns || []);
        var fieldReadOnly_2 = Boolean(readOnly);
        var elementRequired_2 = Boolean(isRequired);
        if (fieldReadOnly_2) {
            return z.array(z.object({}).passthrough());
        }
        var schema_10 = z.array(z.object({}).passthrough());
        var needsRequiredTest = matrixNeedsRequiredTest(elementRequired_2, columns_2, matrixColumnRequiredByRequiredIf);
        if (needsRequiredTest) {
            schema_10 = schema_10.superRefine(function (value, ctx) {
                var valid = validateMatrixDynamicCells(value, columns_2, elementRequired_2, fieldReadOnly_2, matrixColumnDisabledByEnableIf, matrixColumnRequiredByRequiredIf);
                if (!valid) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: getRequiredErrorMessage(element),
                    });
                }
            });
        }
        var needsUniqueTest = matrixNeedsUniqueTest(columns_2, fieldReadOnly_2, matrixColumnDisabledByEnableIf);
        if (needsUniqueTest) {
            schema_10 = schema_10.superRefine(function (value, ctx) {
                var valid = validateMatrixDynamicUniqueColumns(value, columns_2, fieldReadOnly_2, matrixColumnDisabledByEnableIf);
                if (!valid) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: getUniqueColumnErrorMessage(element),
                    });
                }
            });
        }
        return schema_10;
    }
    if (type === SurveyElementTypeEnum.MULTIPLETEXT) {
        var schema_11 = z
            .object({})
            .passthrough()
            .superRefine(function (value, ctx) {
            if (!isRequired)
                return;
            if (!value || typeof value !== "object") {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: getRequiredErrorMessage(element),
                });
                return;
            }
            for (var _i = 0, _a = Object.keys(value); _i < _a.length; _i++) {
                var key = _a[_i];
                if (value[key] === "" ||
                    value[key] == null) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: getRequiredErrorMessage(element),
                    });
                    return;
                }
            }
        });
        return schema_11;
    }
    // Fallback: string genérico
    var schema = z.string();
    if (isRequired && !readOnly) {
        schema = schema.min(1, getRequiredErrorMessage(element));
    }
    return schema;
};
export var buildFieldValidation = function (element, overrides) {
    var _a, _b;
    var baseSchema = buildBaseFieldValidation(element, overrides);
    var readOnly = (_a = overrides.readOnly) !== null && _a !== void 0 ? _a : element.readOnly;
    var validators = ((_b = element.validators) !== null && _b !== void 0 ? _b : []).filter(function (validator) { return validator.type !== ValidatorType.EXPRESSION; });
    if (readOnly || validators.length === 0) {
        return baseSchema;
    }
    return z.any().superRefine(function (value, ctx) {
        var _a;
        var baseResult = baseSchema.safeParse(value);
        if (!baseResult.success) {
            baseResult.error.errors.forEach(function (_a) {
                var message = _a.message;
                return ctx.addIssue({ code: z.ZodIssueCode.custom, message: message });
            });
            return;
        }
        validateValidatorsPageElements([__assign(__assign({}, element), { validators: validators })], (_a = {}, _a[getElementValueKey(element)] = value, _a), {}).forEach(function (result) {
            if (!result.isValid) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: result.errorText || "Regra de validação não atendida",
                });
            }
        });
    });
};
